import React, { useState, useEffect, useMemo } from 'react';
import {
  Phone,
  Mail,
  MessageSquare,
  Building2,
  X,
  Pencil,
  Trash2,
  PhoneCall,
  Calendar,
  CheckSquare,
  FileText,
  Clock,
  AlertCircle,
  Car,
  Zap,
} from 'lucide-react';
import { api } from '../services/api';
import LeadSignalPills from './LeadSignalPills';
import OutboundComposerModal from './OutboundComposerModal';
import OutboundSmsComposerModal from './OutboundSmsComposerModal';
import CalendarInviteModal from './CalendarInviteModal';
import CreateDealModal from './CreateDealModal';

const activityIcons = {
  call: PhoneCall,
  meeting: Calendar,
  task: CheckSquare,
  note: FileText,
};

const outcomeColors = {
  voicemail: '#f59e0b',
  connected: '#10b981',
  scheduled: 'var(--brand-primary, #3b82f6)',
  no_answer: '#ef4444',
};

// ─── Status / pipeline pills ──────────────────────────────────────────────────
const PIPELINE_STAGES = ['new', 'available', 'contacted', 'qualified', 'proposal', 'closed_won', 'closed_lost'];
const stageLabel = (s) => (s || 'new').replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
const stageColor = (s) => {
  const m = {
    new: { bg: 'rgba(148,163,184,0.12)', color: '#94a3b8', border: 'rgba(148,163,184,0.25)' },
    available: { bg: 'rgba(49,126,235,0.12)', color: '#60a5fa', border: 'rgba(49,126,235,0.3)' },
    contacted: { bg: 'rgba(139,92,246,0.12)', color: '#a78bfa', border: 'rgba(139,92,246,0.3)' },
    qualified: { bg: 'rgba(251,191,36,0.12)', color: '#fbbf24', border: 'rgba(251,191,36,0.3)' },
    proposal: { bg: 'rgba(14,165,233,0.12)', color: '#38bdf8', border: 'rgba(14,165,233,0.3)' },
    closed_won: { bg: 'rgba(34,197,94,0.12)', color: '#22c55e', border: 'rgba(34,197,94,0.3)' },
    closed_lost: { bg: 'rgba(239,68,68,0.12)', color: '#ef4444', border: 'rgba(239,68,68,0.3)' },
  };
  return m[s] || m.new;
};

const ContactDetail = ({ contact, onClose, onActivityAdded, onDeleted }) => {
  const [contactData, setContactData] = useState(contact);
  const [leadBriefLoading, setLeadBriefLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('thread');

  // Edit basics
  const [isEditingBasics, setIsEditingBasics] = useState(false);
  const [editFullName, setEditFullName] = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [editPhone, setEditPhone] = useState('');
  const [editSaving, setEditSaving] = useState(false);

  const [deleteBusy, setDeleteBusy] = useState(false);
  const [toast, setToast] = useState(null);

  // Data
  const [activities, setActivities] = useState([]);
  const [conversations, setConversations] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [leadActivities, setLeadActivities] = useState([]);
  const [leadSnapshot, setLeadSnapshot] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [smsThread, setSmsThread] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [emailThread, setEmailThread] = useState([]);
  // eslint-disable-next-line no-unused-vars
  const [smsHasMore, setSmsHasMore] = useState(false);
  // eslint-disable-next-line no-unused-vars
  const [emailHasMore, setEmailHasMore] = useState(false);
  const [unifiedThread, setUnifiedThread] = useState([]);
  const [unifiedHasMore, setUnifiedHasMore] = useState(false);
  const [unifiedView, setUnifiedView] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [showOutboundEmail, setShowOutboundEmail] = useState(false);
  const [showOutboundSms, setShowOutboundSms] = useState(false);
  const [showCalendarInvite, setShowCalendarInvite] = useState(false);
  const [showCreateDeal, setShowCreateDeal] = useState(false);
  const [contactDeals, setContactDeals] = useState([]);
  const [dealsLoading, setDealsLoading] = useState(false);
  const [autopilotSaving, setAutopilotSaving] = useState(false);
  const [newActivity, setNewActivity] = useState({ type: 'call', description: '', outcome: 'connected' });

  const [autopilotEnabled, setAutopilotEnabled] = useState(!!contactData?.autopilot_enabled);
  const [autopilotStatus, setAutopilotStatus] = useState(
    contactData?.autopilot_status || (contactData?.autopilot_enabled ? 'active' : 'disabled')
  );

  // ── helpers ────────────────────────────────────────────────────────────────
  const showToast = (type, text, ms = 2500) => {
    const t = { type, text };
    setToast(t);
    window.clearTimeout(showToast._t);
    showToast._t = window.setTimeout(() => setToast((c) => (c === t ? null : c)), ms);
  };

  const contactDisplayName = useMemo(() => {
    const f = (contactData?.first_name || '').trim();
    const l = (contactData?.last_name || '').trim();
    return `${f} ${l}`.trim() || 'Unknown Lead';
  }, [contactData?.first_name, contactData?.last_name]);

  const contactInitials = useMemo(() => {
    const f = (contactData?.first_name || '').trim();
    const l = (contactData?.last_name || '').trim();
    return (`${f ? f[0] : ''}${l ? l[0] : ''}`).trim() || '?';
  }, [contactData?.first_name, contactData?.last_name]);

  const formatDate = (ds) => {
    const d = new Date(ds);
    const now = new Date();
    const diff = Math.floor((now - d) / 86400000);
    if (diff === 0) return 'Today ' + d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
    if (diff === 1) return 'Yesterday ' + d.toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit' });
    if (diff < 7) return d.toLocaleDateString('en-AU', { weekday: 'short', hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString('en-AU', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getStatusStyle = (status) => {
    const m = {
      PENDING: { background: 'rgba(var(--brand-primary-rgb, 59,130,246),0.15)', color: '#60a5fa' },
      CREDIT_APP: { background: 'rgba(139,92,246,0.15)', color: '#a78bfa' },
      APPROVED: { background: 'rgba(16,185,129,0.15)', color: '#34d399' },
      FUNDING: { background: 'rgba(245,158,11,0.15)', color: '#fbbf24' },
      FUNDED: { background: 'rgba(245,158,11,0.25)', color: '#f59e0b' },
      PREP: { background: 'rgba(99,102,241,0.15)', color: '#818cf8' },
      READY: { background: 'rgba(14,165,233,0.15)', color: '#38bdf8' },
      DELIVERED: { background: 'rgba(34,197,94,0.25)', color: '#22c55e' },
      CANCELLED: { background: 'rgba(239,68,68,0.15)', color: '#ef4444' },
    };
    return m[status] || m.PENDING;
  };

  // ── edit basics ────────────────────────────────────────────────────────────
  const beginEditBasics = () => {
    setEditFullName(`${contactData?.first_name || ''} ${contactData?.last_name || ''}`.trim());
    setEditEmail(contactData?.email || '');
    setEditPhone(contactData?.phone || '');
    setIsEditingBasics(true);
  };
  const cancelEditBasics = () => { setIsEditingBasics(false); setEditSaving(false); };
  const saveBasics = async () => {
    const fullName = String(editFullName || '').trim();
    if (!fullName) { showToast('error', 'Name is required.'); return; }
    setEditSaving(true);
    try {
      const updated = await api.patchContact(contact.id, {
        full_name: fullName,
        email: String(editEmail || '').trim() || null,
        phone: String(editPhone || '').trim() || null,
      });
      setContactData((p) => ({ ...(p || {}), ...(updated || {}) }));
      setIsEditingBasics(false);
      showToast('success', 'Lead updated.');
    } catch (e) {
      showToast('error', e?.message || 'Failed to update lead');
    } finally {
      setEditSaving(false);
    }
  };

  const handleDeleteLead = async () => {
    if (!contact?.id || deleteBusy) return;
    if (!window.confirm('Delete this lead? This cannot be undone.')) return;
    setDeleteBusy(true);
    try {
      await api.deleteContact(contact.id);
      showToast('success', 'Lead deleted');
      typeof onDeleted === 'function' && onDeleted(contact.id);
      window.setTimeout(() => onClose?.(), 250);
    } catch (e) {
      showToast('error', e?.message || 'Failed to delete lead');
    } finally {
      setDeleteBusy(false);
    }
  };

  // ── data loading ───────────────────────────────────────────────────────────
  useEffect(() => {
    setAutopilotEnabled(!!contactData?.autopilot_enabled);
    setAutopilotStatus(contactData?.autopilot_status || (contactData?.autopilot_enabled ? 'active' : 'disabled'));
  }, [contactData?.autopilot_enabled, contactData?.autopilot_status]);

  useEffect(() => {
    api.getContact(contact.id).then((d) => setContactData(d)).catch(console.error);
  }, [contact.id]);

  useEffect(() => {
    setIsLoading(true);
    Promise.all([
      api.getContactActivities(contact.id),
      api.getContactConversations(contact.id),
      api.getContactLeadActivities(contact.id),
      api.getContactLeadSnapshot(contact.id),
      api.getContactSmsThread(contact.id, { limit: 50 }),
      api.getContactEmailThread(contact.id, { limit: 50 }),
      api.getContactUnifiedThread(contact.id, { limit: 50, view: unifiedView }),
    ]).then(([acts, convs, leadActs, snap, smsMsgs, emailMsgs, unifiedMsgs]) => {
      setActivities(acts);
      setConversations(convs);
      setLeadActivities(leadActs);
      setLeadSnapshot(snap?.snapshot || null);
      const smsArr = Array.isArray(smsMsgs) ? smsMsgs : [];
      const emailArr = Array.isArray(emailMsgs) ? emailMsgs : [];
      setSmsThread(smsArr); setSmsHasMore(smsArr.length >= 50);
      setEmailThread(emailArr); setEmailHasMore(emailArr.length >= 50);
      const uArr = Array.isArray(unifiedMsgs) ? unifiedMsgs : [];
      setUnifiedThread(uArr); setUnifiedHasMore(uArr.length >= 50);
    }).catch(console.error).finally(() => setIsLoading(false));
  }, [contact.id, unifiedView]);

  const loadContactDeals = async () => {
    setDealsLoading(true);
    try {
      const d = await api.getDeals({ contact_id: contact.id });
      setContactDeals(d.deals || []);
    } catch (e) { console.error(e); } finally { setDealsLoading(false); }
  };
  useEffect(() => { loadContactDeals(); }, [contact.id]);

  const refreshActivities = async () => {
    try { setActivities(await api.getContactActivities(contact.id)); } catch (e) { console.error(e); }
  };

  const refreshUnifiedThread = async () => {
    try {
      const msgs = await api.getContactUnifiedThread(contact.id, { limit: 50, view: unifiedView });
      const arr = Array.isArray(msgs) ? msgs : [];
      setUnifiedThread(arr); setUnifiedHasMore(arr.length >= 50);
    } catch (e) { console.error(e); }
  };

  // eslint-disable-next-line no-unused-vars
  const refreshSmsThread = async () => {
    try {
      const msgs = await api.getContactSmsThread(contact.id, { limit: 50 });
      const arr = Array.isArray(msgs) ? msgs : [];
      setSmsThread(arr); setSmsHasMore(arr.length >= 50);
    } catch (e) { console.error(e); }
  };

  // eslint-disable-next-line no-unused-vars
  const refreshEmailThread = async () => {
    try {
      const msgs = await api.getContactEmailThread(contact.id, { limit: 50 });
      const arr = Array.isArray(msgs) ? msgs : [];
      setEmailThread(arr); setEmailHasMore(arr.length >= 50);
    } catch (e) { console.error(e); }
  };

  const loadOlderUnified = async () => {
    if (!unifiedThread.length) return;
    const oldest = unifiedThread.reduce((min, m) => {
      const t = m?.created_at ? new Date(m.created_at).getTime() : Infinity;
      const mt = min?.created_at ? new Date(min.created_at).getTime() : Infinity;
      return t < mt ? m : min;
    }, unifiedThread[0]);
    try {
      const msgs = await api.getContactUnifiedThread(contact.id, { limit: 50, before: oldest?.created_at, view: unifiedView });
      const arr = Array.isArray(msgs) ? msgs : [];
      const merged = [...unifiedThread];
      for (const m of arr) { if (!merged.find((x) => x.id === m.id)) merged.push(m); }
      setUnifiedThread(merged); setUnifiedHasMore(arr.length >= 50);
    } catch (e) { console.error(e); }
  };

  const cancelPlannedMessage = async (channel, id) => {
    try {
      await api.cancelContactPlannedMessage(contact.id, id);
      setUnifiedThread((p) => p.filter((m) => m.id !== id));
      if (channel === 'sms') setSmsThread((p) => p.filter((m) => m.id !== id));
      else setEmailThread((p) => p.filter((m) => m.id !== id));
    } catch (e) { alert(e?.message || 'Failed to cancel'); }
  };

  const updateAutopilot = async (patch) => {
    setAutopilotSaving(true);
    try {
      const updated = await api.updateContactAutopilot(contact.id, patch);
      setContactData((p) => ({ ...(p || {}), ...(updated || {}) }));
      setAutopilotEnabled(!!updated.autopilot_enabled);
      setAutopilotStatus(updated.autopilot_status || (updated.autopilot_enabled ? 'active' : 'disabled'));
    } catch (e) { alert(e?.message || 'Failed to update autopilot'); } finally { setAutopilotSaving(false); }
  };

  const updateDealStatus = async (dealId, newStatus) => {
    try {
      await api.request(`/api/deals/${dealId}`, { method: 'PATCH', body: JSON.stringify({ status: newStatus }) });
      showToast('success', `Deal updated to ${newStatus}`);
      loadContactDeals();
    } catch (e) { showToast('error', 'Failed to update deal'); }
  };

  const handleAddActivity = async (e) => {
    e.preventDefault();
    try {
      await api.createActivity(contact.id, newActivity);
      setNewActivity({ type: 'call', description: '', outcome: 'connected' });
      setShowAddForm(false);
      refreshActivities();
      if (onActivityAdded) onActivityAdded();
    } catch (e) { console.error(e); }
  };

  const handleRegenerateLeadBrief = async () => {
    setLeadBriefLoading(true);
    try {
      const resp = await api.regenerateLeadBrief(contact.id);
      setContactData((p) => ({ ...(p || {}), lead_brief: resp?.lead_brief, lead_brief_updated_at: resp?.lead_brief_updated_at }));
    } catch (e) { alert(e?.message || 'Failed to regenerate'); } finally { setLeadBriefLoading(false); }
  };

  const handleCopyLeadBrief = async () => {
    try { await navigator.clipboard.writeText((contactData?.lead_brief || '').trim()); }
    catch (e) { alert('Copy failed'); }
  };

  const whyHotSummary = (contactData?.why_hot_summary || '').trim();

  // ── render ─────────────────────────────────────────────────────────────────
  const sc = stageColor(contactData?.status);
  const TABS = [
    { id: 'thread', label: 'Thread' },
    { id: 'brief', label: 'AI Brief' },
    { id: 'deals', label: `Deals${contactDeals.length ? ` (${contactDeals.length})` : ''}` },
    { id: 'convos', label: `Conversations${conversations.length ? ` (${conversations.length})` : ''}` },
    { id: 'activities', label: 'Activities' },
  ];

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.modal} onClick={(e) => e.stopPropagation()}>

        {/* Toast */}
        {toast && (
          <div style={{
            ...S.toast,
            background: toast.type === 'success' ? 'rgba(34,197,94,0.16)' : 'rgba(239,68,68,0.18)',
            borderColor: toast.type === 'success' ? 'rgba(34,197,94,0.45)' : 'rgba(239,68,68,0.45)',
            color: toast.type === 'success' ? '#bbf7d0' : '#fca5a5',
          }} role="status">
            <div>
              <div style={{ fontWeight: 800 }}>{toast.type === 'success' ? 'Saved' : 'Error'}</div>
              <div style={{ marginTop: 2 }}>{toast.text}</div>
            </div>
            <button onClick={() => setToast(null)} style={S.toastClose}>✕</button>
          </div>
        )}

        {/* ── Header row ───────────────────────────────────────────────────── */}
        <div style={S.header}>
          <div style={S.headerLeft}>
            <div style={S.avatar}>{contactInitials}</div>
            <div style={{ minWidth: 0 }}>
              <div style={S.name}>{contactDisplayName}</div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 4, flexWrap: 'wrap' }}>
                {/* Pipeline stage pill */}
                <span style={{ ...S.pill, background: sc.bg, color: sc.color, borderColor: sc.border }}>
                  {stageLabel(contactData?.status)}
                </span>
                {/* HOT badge — once only */}
                {contactData?.hot_lead && (
                  <span style={{ ...S.pill, background: 'rgba(239,68,68,0.2)', color: '#fecaca', borderColor: 'rgba(239,68,68,0.35)', fontWeight: 800 }}>
                    HOT {typeof contactData?.hot_score === 'number' ? contactData.hot_score : ''}
                  </span>
                )}
                {contactData?.source && (
                  <span style={{ ...S.pill, fontSize: 11, opacity: 0.7 }}>{contactData.source}</span>
                )}
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexShrink: 0 }}>
            {!isEditingBasics && (
              <>
                <button onClick={beginEditBasics} style={S.iconBtn} title="Edit lead">
                  <Pencil size={15} />
                </button>
                <button onClick={handleDeleteLead} disabled={deleteBusy} style={{ ...S.iconBtn, color: '#fca5a5' }} title="Delete lead">
                  <Trash2 size={15} />
                </button>
              </>
            )}
            <button onClick={onClose} style={{ ...S.iconBtn, marginLeft: 4 }}><X size={20} /></button>
          </div>
        </div>

        {/* ── Edit form (inline, full-width) ───────────────────────────────── */}
        {isEditingBasics && (
          <div style={S.editCard}>
            <div style={S.editRow}>
              <label style={S.editLabel}>Full name</label>
              <input value={editFullName} onChange={(e) => setEditFullName(e.target.value)} style={S.editInput} placeholder="Jane Smith" autoFocus />
            </div>
            <div style={S.editRow}>
              <label style={S.editLabel}>Email</label>
              <input value={editEmail} onChange={(e) => setEditEmail(e.target.value)} style={S.editInput} placeholder="name@company.com" />
            </div>
            <div style={S.editRow}>
              <label style={S.editLabel}>Mobile / phone</label>
              <input value={editPhone} onChange={(e) => setEditPhone(e.target.value)} style={S.editInput} placeholder="04…" />
            </div>
            <div style={S.editActions}>
              <button onClick={cancelEditBasics} style={S.cancelBtn} disabled={editSaving}>Cancel</button>
              <button onClick={saveBasics} style={{ ...S.saveBtn, opacity: editSaving ? 0.7 : 1 }} disabled={editSaving}>
                {editSaving ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        )}

        {/* ── Contact details row ───────────────────────────────────────────── */}
        <div style={S.contactRow}>
          {contactData?.phone && (
            <a href={`tel:${contactData.phone}`} style={S.contactItem}>
              <Phone size={13} style={{ color: '#64748b' }} />{contactData.phone}
            </a>
          )}
          {contactData?.email && (
            <a href={`mailto:${contactData.email}`} style={S.contactItem}>
              <Mail size={13} style={{ color: '#64748b' }} />{contactData.email}
            </a>
          )}
          {contactData?.company_name && (
            <span style={S.contactItem}>
              <Building2 size={13} style={{ color: '#64748b' }} />{contactData.company_name}
            </span>
          )}
          {contactData?.sms_opted_out && (
            <span style={{ ...S.pill, background: 'rgba(239,68,68,0.12)', color: '#fecaca', borderColor: 'rgba(239,68,68,0.35)', fontSize: 11 }}>
              SMS opted out
            </span>
          )}
        </div>

        {/* ── Lead-signal pill row ─────────────────────────────────────────── */}
        {/* Renders Intent / Timeline / Trade / Finance pills from structured_data.
            Pill visibility rules per Quill-approved design spec:
              - intent_score: show if NOT null (0 is a valid real value)
              - purchase_timeline: show if not null
              - has_trade_in: show "Trade: Y" only when true; suppress on false
              - finance_preference: show if not null
            Zero-pill state (all null/false) renders nothing. */}
        <LeadSignalPills
          intent_score={contactData?.intent_score ?? null}
          purchase_timeline={contactData?.purchase_timeline ?? null}
          has_trade_in={contactData?.has_trade_in ?? false}
          finance_preference={contactData?.finance_preference ?? null}
        />

        {/* ── Snapshot chips ────────────────────────────────────────────────── */}
        {leadSnapshot && (
          <div style={S.chips}>
            {typeof leadSnapshot.intent_score === 'number' && <span style={S.chip}>Intent {leadSnapshot.intent_score}</span>}
            {leadSnapshot.purchase_timeline && <span style={S.chip}>{leadSnapshot.purchase_timeline}</span>}
            {(leadSnapshot.budget_min || leadSnapshot.budget_max) && (
              <span style={S.chip}>
                {leadSnapshot.budget_min || ''}{leadSnapshot.budget_min && leadSnapshot.budget_max ? '–' : ''}{leadSnapshot.budget_max || ''}
              </span>
            )}
            {leadSnapshot.vehicle_type && <span style={S.chip}>{leadSnapshot.vehicle_type}</span>}
            {typeof leadSnapshot.has_trade_in === 'boolean' && <span style={S.chip}>Trade: {leadSnapshot.has_trade_in ? 'Y' : 'N'}</span>}
            {leadSnapshot.finance_preference && <span style={S.chip}>{leadSnapshot.finance_preference}</span>}
            {leadSnapshot.next_action && <span style={S.chip}>{leadSnapshot.next_action}</span>}
            {whyHotSummary && (
              <span style={{ ...S.chip, maxWidth: 220, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                title={whyHotSummary}>Why: {whyHotSummary}</span>
            )}
          </div>
        )}

        {/* ── Next Appointment ─────────────────────────────────────────────── */}
        {contactData?.next_appointment_at && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 20px', background: 'rgba(49,126,235,0.08)', borderTop: '1px solid rgba(49,126,235,0.18)' }}>
            <Calendar size={13} style={{ color: 'var(--brand-primary, #317eeb)', flexShrink: 0 }} />
            <span style={{ fontSize: 12, color: '#94a3b8', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px' }}>
              {contactData.next_appointment_type === 'test_drive' ? 'Test Drive' : contactData.next_appointment_type === 'service' ? 'Service' : 'Appointment'}
            </span>
            <span style={{ fontSize: 13, color: '#e2e8f0', marginLeft: 4 }}>
              {new Date(contactData.next_appointment_at).toLocaleString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' })}
            </span>
          </div>
        )}

        {/* ── Autopilot toggle (single row) ─────────────────────────────────── */}
        <div style={S.autopilotRow}>
          <Zap size={14} style={{ color: autopilotEnabled ? '#fbbf24' : '#475569', flexShrink: 0 }} />
          <span style={{ color: '#94a3b8', fontSize: 13 }}>Autopilot</span>
          <label style={S.toggleWrap}>
            <input
              type="checkbox"
              checked={!!autopilotEnabled}
              disabled={autopilotSaving}
              onChange={(e) => updateAutopilot({ autopilot_status: e.target.checked ? 'active' : 'disabled' })}
              style={{ cursor: 'pointer' }}
            />
            <span style={{ color: autopilotEnabled ? '#22c55e' : '#64748b', fontWeight: 700, fontSize: 13 }}>
              {autopilotSaving ? '…' : autopilotEnabled ? 'On' : 'Off'}
            </span>
          </label>
          <span style={{ marginLeft: 'auto', color: '#475569', fontSize: 11 }}>
            Vinnie runs autonomously until lead is hot
          </span>
        </div>

        {/* ── Action bar ────────────────────────────────────────────────────── */}
        <div style={S.actions}>
          {[
            { label: 'Email', icon: Mail, disabled: !contactData?.email, action: () => setShowOutboundEmail(true) },
            { label: 'SMS', icon: MessageSquare, disabled: !contactData?.phone, action: () => setShowOutboundSms(true) },
            { label: 'Calendar', icon: Calendar, disabled: !contactData?.email, action: () => setShowCalendarInvite(true) },
            { label: 'Call', icon: PhoneCall, action: () => { setNewActivity({ ...newActivity, type: 'call' }); setShowAddForm(true); } },
            { label: 'Note', icon: FileText, action: () => { setNewActivity({ ...newActivity, type: 'note' }); setShowAddForm(true); } },
            { label: 'Deal', icon: Car, action: () => setShowCreateDeal(true) },
          ].map(({ label, icon: Icon, disabled, action }) => (
            <button key={label} onClick={action} disabled={!!disabled} style={{ ...S.actionBtn, opacity: disabled ? 0.4 : 1 }}>
              <Icon size={14} />
              <span>{label}</span>
            </button>
          ))}
        </div>

        {/* ── Add activity form ─────────────────────────────────────────────── */}
        {showAddForm && (
          <form style={S.addForm} onSubmit={handleAddActivity}>
            <select style={S.select} value={newActivity.type} onChange={(e) => setNewActivity({ ...newActivity, type: e.target.value })}>
              <option value="call">Call</option>
              <option value="meeting">Meeting</option>
              <option value="task">Task</option>
              <option value="note">Note</option>
            </select>
            {newActivity.type === 'call' && (
              <select style={S.select} value={newActivity.outcome} onChange={(e) => setNewActivity({ ...newActivity, outcome: e.target.value })}>
                <option value="connected">Connected</option>
                <option value="voicemail">Voicemail</option>
                <option value="no_answer">No Answer</option>
                <option value="scheduled">Scheduled</option>
              </select>
            )}
            <textarea style={S.textarea} placeholder="Add details…" rows={3} value={newActivity.description}
              onChange={(e) => setNewActivity({ ...newActivity, description: e.target.value })} />
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="button" style={S.cancelBtn} onClick={() => setShowAddForm(false)}>Cancel</button>
              <button type="submit" style={S.saveBtn}>Save</button>
            </div>
          </form>
        )}

        {/* ── Tabs ──────────────────────────────────────────────────────────── */}
        <div style={S.tabBar}>
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={{ ...S.tab, ...(activeTab === t.id ? S.tabActive : {}) }}>
              {t.label}
            </button>
          ))}
        </div>

        {/* ── Tab content ───────────────────────────────────────────────────── */}
        <div style={S.tabContent}>

          {/* THREAD */}
          {activeTab === 'thread' && (
            <div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                {['all', 'past', 'planned'].map((v) => (
                  <button key={v} onClick={() => setUnifiedView(v)} style={{
                    ...S.filterBtn,
                    background: unifiedView === v ? 'var(--brand-primary, #317eeb)' : 'transparent',
                    color: unifiedView === v ? '#fff' : '#94a3b8',
                  }}>{v.charAt(0).toUpperCase() + v.slice(1)}</button>
                ))}
                <span style={{ marginLeft: 'auto', color: '#475569', fontSize: 12 }}>{unifiedThread.length} messages</span>
                <button onClick={refreshUnifiedThread} style={S.filterBtn}>Refresh</button>
              </div>

              {isLoading ? (
                <p style={S.dim}>Loading…</p>
              ) : unifiedThread.length === 0 ? (
                <p style={S.dim}>No messages yet.</p>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                  {[...unifiedThread]
                    .sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0))
                    .map((m) => {
                      const isPlanned = !!m?.is_planned;
                      const isSms = m.channel === 'sms';
                      const isChat = m.channel === 'chat';
                      const borderColor = isSms ? 'var(--brand-primary, #3b82f6)' : isChat ? '#a855f7' : '#10b981';
                      const bg = isSms
                        ? (isPlanned ? 'rgba(var(--brand-primary-rgb, 59,130,246),0.06)' : 'rgba(var(--brand-primary-rgb, 59,130,246),0.10)')
                        : isChat
                          ? 'rgba(168,85,247,0.10)'
                          : (isPlanned ? 'rgba(16,185,129,0.06)' : 'rgba(16,185,129,0.10)');
                      return (
                        <div key={m.id} style={{ border: `1px ${isPlanned ? 'dashed' : 'solid'} ${borderColor}`, borderRadius: 10, padding: 12, background: bg }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                              <span style={{ fontSize: 11, fontWeight: 800, color: '#e2e8f0', textTransform: 'uppercase' }}>
                                {isChat ? 'Chat' : m.direction === 'inbound' ? 'In' : 'Out'} · {m.channel}
                              </span>
                              {isPlanned && <span style={S.plannedBadge}>Planned</span>}
                            </div>
                            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                              <span style={{ color: '#64748b', fontSize: 11 }}>{formatDate(m.scheduled_for || m.created_at)}</span>
                              {isPlanned && (
                                <button onClick={() => cancelPlannedMessage(m.channel, m.id)} style={S.stopBtn}>Stop</button>
                              )}
                            </div>
                          </div>
                          {m.subject && <div style={{ marginTop: 6, color: '#cbd5e1', fontSize: 13, fontWeight: 700 }}>{m.subject}</div>}
                          {m.body && <div style={{ marginTop: 4, color: '#94a3b8', fontSize: 13, whiteSpace: 'pre-wrap' }}>{m.body}</div>}
                          {m.channel === 'chat' && m.metadata?.transcript_preview && (
                            <details style={{ marginTop: 8 }}>
                              <summary style={{ cursor: 'pointer', color: '#a78bfa', fontSize: 12 }}>View transcript</summary>
                              <pre style={{ whiteSpace: 'pre-wrap', color: '#cbd5e1', marginTop: 6, fontSize: 12 }}>{m.metadata.transcript_preview}</pre>
                            </details>
                          )}
                        </div>
                      );
                    })}
                  {unifiedHasMore && (
                    <button onClick={loadOlderUnified} style={{ ...S.filterBtn, alignSelf: 'flex-start' }}>Load older</button>
                  )}
                </div>
              )}
            </div>
          )}

          {/* AI BRIEF */}
          {activeTab === 'brief' && (
            <div>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
                <button onClick={handleRegenerateLeadBrief} disabled={leadBriefLoading} style={S.filterBtn}>
                  {leadBriefLoading ? 'Regenerating…' : 'Regenerate'}
                </button>
                <button onClick={handleCopyLeadBrief} disabled={!contactData?.lead_brief} style={S.filterBtn}>Copy</button>
                {contactData?.lead_brief_updated_at && (
                  <span style={{ marginLeft: 'auto', color: '#475569', fontSize: 11, alignSelf: 'center' }}>
                    Updated {formatDate(contactData.lead_brief_updated_at)}
                  </span>
                )}
              </div>
              <pre style={S.briefPre}>
                {(contactData?.lead_brief || 'No lead brief yet. Tap Regenerate.').trim()}
              </pre>
              {contactData?.hot_reasons?.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={S.sectionLabel}>Why hot</div>
                  <ul style={{ margin: 0, paddingLeft: 18, color: '#cbd5e1', fontSize: 13, lineHeight: 1.5 }}>
                    {contactData.hot_reasons.map((r, i) => (
                      <li key={i}>
                        <strong>{r.signal || 'signal'}</strong>
                        {typeof r.points === 'number' ? ` (+${r.points})` : ''}
                        {r.evidence ? ` — ${r.evidence}` : ''}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          )}

          {/* DEALS */}
          {activeTab === 'deals' && (
            <div>
              <button onClick={() => setShowCreateDeal(true)} style={{ ...S.filterBtn, marginBottom: 12 }}>+ New deal</button>
              {dealsLoading ? <p style={S.dim}>Loading deals…</p>
                : contactDeals.length === 0 ? <p style={S.dim}>No deals yet.</p>
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {contactDeals.map((deal) => (
                      <div key={deal.id} style={S.dealCard}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                          <span style={{ fontWeight: 600, color: '#f1f5f9', fontSize: 14 }}>
                            {deal.vehicle.year} {deal.vehicle.make} {deal.vehicle.model}
                          </span>
                          <span style={{ color: '#22c55e', fontWeight: 700, fontSize: 14 }}>
                            ${deal.economics.sale_price?.toLocaleString()}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          <span style={{ ...getStatusStyle(deal.status), padding: '3px 8px', borderRadius: 6, fontSize: 12, fontWeight: 600 }}>
                            {deal.status}
                          </span>
                          <select value={deal.status} onChange={(e) => updateDealStatus(deal.id, e.target.value)} style={S.statusSelect}>
                            {['PENDING','CREDIT_APP','APPROVED','FUNDING','FUNDED','PREP','READY','DELIVERED','CANCELLED'].map((s) => (
                              <option key={s} value={s}>{s}</option>
                            ))}
                          </select>
                          {deal.economics.gross_profit && (
                            <span style={{ color: '#64748b', fontSize: 12, marginLeft: 'auto' }}>
                              Gross: ${deal.economics.gross_profit.toLocaleString()}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
            </div>
          )}

          {/* CONVERSATIONS */}
          {activeTab === 'convos' && (
            <div>
              {isLoading ? <p style={S.dim}>Loading…</p>
                : conversations.length === 0 ? <p style={S.dim}>No conversations yet.</p>
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {conversations.map((c) => (
                      <div key={c.id} style={{ ...S.dealCard, borderColor: '#334155' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                          <span style={{ fontWeight: 600, color: '#f1f5f9', fontSize: 13 }}>Conversation</span>
                          {c.started_at && <span style={{ color: '#64748b', fontSize: 11 }}>{formatDate(c.started_at)}</span>}
                        </div>
                        {c.summary && <p style={{ margin: 0, color: '#94a3b8', fontSize: 13 }}>{c.summary}</p>}
                        {c.transcript_text && (
                          <details style={{ marginTop: 8 }}>
                            <summary style={{ cursor: 'pointer', color: '#60a5fa', fontSize: 12 }}>View transcript</summary>
                            <pre style={{ whiteSpace: 'pre-wrap', color: '#cbd5e1', marginTop: 6, fontSize: 12 }}>{c.transcript_text}</pre>
                          </details>
                        )}
                      </div>
                    ))}
                  </div>
                )}
            </div>
          )}

          {/* CRM ACTIVITIES */}
          {activeTab === 'activities' && (
            <div>
              {isLoading ? <p style={S.dim}>Loading…</p>
                : activities.length === 0 ? <p style={S.dim}>No activities yet. Log a call or add a note.</p>
                : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                    {activities.map((activity) => {
                      const Icon = activityIcons[activity.type] || FileText;
                      return (
                        <div key={activity.id} style={{ ...S.dealCard, display: 'flex', gap: 12, alignItems: 'flex-start' }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--brand-primary, #3b82f6)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                            <Icon size={14} color="#fff" />
                          </div>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                              <span style={{ fontWeight: 600, color: '#f1f5f9', fontSize: 13, textTransform: 'capitalize' }}>{activity.type}</span>
                              {activity.outcome && (
                                <span style={{ padding: '1px 6px', borderRadius: 999, fontSize: 11, fontWeight: 600, color: '#fff', background: outcomeColors[activity.outcome] || '#6b7280' }}>
                                  {activity.outcome}
                                </span>
                              )}
                              <span style={{ marginLeft: 'auto', color: '#64748b', fontSize: 11 }}>{formatDate(activity.created_at)}</span>
                            </div>
                            {activity.description && <p style={{ margin: 0, color: '#94a3b8', fontSize: 13 }}>{activity.description}</p>}
                            {activity.follow_up_date && (
                              <div style={{ marginTop: 6, display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 8px', borderRadius: 999, fontSize: 11,
                                background: new Date(activity.follow_up_date) < new Date() ? 'rgba(239,68,68,0.2)' : 'rgba(var(--brand-primary-rgb, 59,130,246),0.2)',
                                color: new Date(activity.follow_up_date) < new Date() ? '#fca5a5' : '#93c5fd' }}>
                                <AlertCircle size={11} />
                                Follow-up: {new Date(activity.follow_up_date).toLocaleDateString()}
                              </div>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
            </div>
          )}
        </div>

        {/* ── Modals ────────────────────────────────────────────────────────── */}
        <OutboundComposerModal open={showOutboundEmail} contact={contact}
          onClose={(res) => { setShowOutboundEmail(false); if (res?.sent) { refreshUnifiedThread(); refreshActivities(); } }} />
        <OutboundSmsComposerModal open={showOutboundSms} contact={contact}
          onClose={(res) => { setShowOutboundSms(false); if (res?.sent) { refreshUnifiedThread(); refreshActivities(); } }} />
        <CalendarInviteModal open={showCalendarInvite} contact={contact}
          onClose={(res) => { setShowCalendarInvite(false); if (res?.sent) refreshActivities(); }} />
        {showCreateDeal && (
          <CreateDealModal contact={contactData}
            onClose={() => { setShowCreateDeal(false); loadContactDeals(); }}
            onDealCreated={(deal) => { showToast('success', `Deal: ${deal.vehicle.make} ${deal.vehicle.model}`); loadContactDeals(); }} />
        )}
      </div>
    </div>
  );
};

// ─── Styles ───────────────────────────────────────────────────────────────────
const S = {
  overlay: {
    position: 'fixed', inset: 0,
    background: 'rgba(0,0,0,0.75)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000,
    padding: '12px',
  },
  modal: {
    width: '100%',
    maxWidth: '680px',
    maxHeight: '92vh',
    background: '#141b2f',
    border: '1px solid #2c354e',
    borderRadius: '16px',
    display: 'flex',
    flexDirection: 'column',
    overflow: 'hidden',
    boxShadow: '0 24px 64px rgba(0,0,0,0.55)',
  },
  toast: {
    margin: '10px 16px 0',
    padding: '10px 12px',
    borderRadius: 10, border: '1px solid',
    display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12,
    fontSize: 13,
  },
  toastClose: { background: 'transparent', border: 'none', color: 'inherit', cursor: 'pointer', fontSize: 15, padding: 2 },
  header: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    padding: '16px 20px', borderBottom: '1px solid #2c354e',
  },
  headerLeft: { display: 'flex', alignItems: 'center', gap: 14, minWidth: 0 },
  avatar: {
    width: 48, height: 48, borderRadius: '50%',
    background: 'linear-gradient(135deg,var(--brand-primary, #317eeb) 0%,#8b5cf6 100%)',
    color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
    fontSize: 18, fontWeight: 800, flexShrink: 0,
  },
  name: { margin: 0, fontSize: 18, fontWeight: 700, color: '#f1f5f9', lineHeight: 1.2 },
  pill: {
    display: 'inline-flex', alignItems: 'center',
    padding: '3px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700,
    border: '1px solid', letterSpacing: '0.02em',
  },
  iconBtn: {
    background: 'transparent', border: '1px solid #2c354e', borderRadius: 8,
    color: '#94a3b8', cursor: 'pointer', padding: '6px 8px',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  },
  editCard: { margin: '0 20px 0', padding: '14px 16px', background: 'rgba(2,6,23,0.4)', borderBottom: '1px solid #2c354e' },
  editRow: { display: 'flex', flexDirection: 'column', gap: 5, marginBottom: 10 },
  editLabel: { fontSize: 11, fontWeight: 800, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' },
  editInput: { padding: '9px 12px', background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
  editActions: { display: 'flex', justifyContent: 'flex-end', gap: 10 },
  contactRow: {
    display: 'flex', flexWrap: 'wrap', gap: '8px 16px',
    padding: '10px 20px', borderBottom: '1px solid #2c354e',
  },
  contactItem: {
    display: 'inline-flex', alignItems: 'center', gap: 5,
    fontSize: 13, color: '#94a3b8', textDecoration: 'none',
  },
  chips: {
    display: 'flex', flexWrap: 'wrap', gap: 6,
    padding: '10px 20px', borderBottom: '1px solid #2c354e',
  },
  chip: {
    fontSize: 11, color: '#94a3b8',
    background: 'rgba(148,163,184,0.10)', border: '1px solid rgba(148,163,184,0.18)',
    padding: '3px 8px', borderRadius: 999,
  },
  autopilotRow: {
    display: 'flex', alignItems: 'center', gap: 10,
    padding: '10px 20px', borderBottom: '1px solid #2c354e',
    background: 'rgba(2,6,23,0.25)',
  },
  toggleWrap: { display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' },
  actions: {
    display: 'flex', gap: 6, padding: '10px 20px',
    borderBottom: '1px solid #2c354e', flexWrap: 'wrap',
  },
  actionBtn: {
    display: 'flex', alignItems: 'center', gap: 5,
    padding: '8px 12px', background: '#212942', border: '1px solid #2c354e',
    borderRadius: 8, color: '#e2e8f0', fontSize: 13, fontWeight: 600,
    cursor: 'pointer', flexShrink: 0,
  },
  addForm: { padding: '12px 20px', background: 'rgba(2,6,23,0.3)', borderBottom: '1px solid #2c354e' },
  select: { width: '100%', padding: '9px 12px', marginBottom: 10, background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 13, boxSizing: 'border-box' },
  textarea: { width: '100%', padding: '9px 12px', marginBottom: 10, background: '#0f172a', border: '1px solid #334155', borderRadius: 8, color: '#f1f5f9', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' },
  cancelBtn: { flex: 1, padding: '9px 14px', background: 'transparent', border: '1px solid #475569', borderRadius: 8, color: '#94a3b8', fontSize: 13, cursor: 'pointer' },
  saveBtn: { flex: 1, padding: '9px 14px', background: 'var(--brand-primary, #317eeb)', border: 'none', borderRadius: 8, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer' },
  tabBar: {
    display: 'flex', gap: 2, padding: '0 20px',
    borderBottom: '1px solid #2c354e', overflowX: 'auto',
    flexShrink: 0,
  },
  tab: {
    padding: '10px 14px', background: 'transparent', border: 'none',
    borderBottom: '2px solid transparent', color: '#64748b',
    fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
    transition: 'color 0.15s',
  },
  tabActive: { color: '#f1f5f9', borderBottomColor: 'var(--brand-primary, #317eeb)' },
  tabContent: { flex: 1, overflowY: 'auto', padding: '16px 20px' },
  filterBtn: {
    padding: '5px 12px', background: 'transparent', border: '1px solid #2c354e',
    borderRadius: 8, color: '#94a3b8', fontSize: 12, fontWeight: 600, cursor: 'pointer',
  },
  dim: { color: '#475569', fontSize: 13, margin: 0 },
  briefPre: {
    whiteSpace: 'pre-wrap', fontSize: 13, lineHeight: 1.5, color: '#e2e8f0',
    background: 'rgba(2,6,23,0.35)', border: '1px solid #2c354e',
    borderRadius: 10, padding: 14, margin: 0, maxHeight: 300, overflowY: 'auto',
  },
  sectionLabel: { fontSize: 11, fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.05em', color: '#475569', marginBottom: 6 },
  plannedBadge: { fontSize: 10, fontWeight: 800, color: '#fbbf24', background: 'rgba(251,191,36,0.15)', border: '1px solid rgba(251,191,36,0.35)', padding: '2px 6px', borderRadius: 999 },
  stopBtn: { background: 'transparent', border: '1px solid rgba(248,113,113,0.6)', color: '#fca5a5', borderRadius: 999, padding: '2px 7px', fontSize: 11, fontWeight: 700, cursor: 'pointer' },
  dealCard: { background: 'rgba(33,41,66,0.6)', border: '1px solid #2c354e', borderRadius: 10, padding: '12px 14px' },
  statusSelect: { padding: '5px 8px', background: '#0f172a', border: '1px solid #334155', borderRadius: 6, color: '#f1f5f9', fontSize: 12, cursor: 'pointer' },
};

export default ContactDetail;
